import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';

const ShareModal = ({ isOpen, onClose, shareableLink, gameTitle }) => {
  const [copied, setCopied] = useState(false);
  
  if (!isOpen) return null;
  
  const handleCopyLink = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(shareableLink)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(err => {
          console.error('Failed to copy: ', err);
        });
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareableLink;
      textArea.style.position = 'fixed';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy: ', err);
      }
      
      document.body.removeChild(textArea);
    }
  };
  
  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Share Your Game</ModalTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>
        
        <ModalBody>
          <GameInfoSection>
            <GameTitle>{gameTitle || 'Untitled Game'}</GameTitle>
            <GameDescription>
              Your game has been published and is ready to share!
            </GameDescription>
          </GameInfoSection>
          
          <ShareLinkSection>
            <ShareLinkLabel>Game Link:</ShareLinkLabel>
            <ShareLinkContainer>
              <ShareLink value={shareableLink} readOnly />
              <CopyButton onClick={handleCopyLink}>
                {copied ? 'Copied!' : 'Copy'}
              </CopyButton>
            </ShareLinkContainer>
          </ShareLinkSection>
          
          <SocialShareSection>
            <SocialShareLabel>Share on:</SocialShareLabel>
            <SocialButtons>
              <SocialButton 
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareableLink)}&text=${encodeURIComponent(`Check out my game: ${gameTitle || 'Untitled Game'}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                color="#1DA1F2"
                aria-label="Share on Twitter"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 0 1-1.93.07 4.28 4.28 0 0 0 4 2.98 8.521 8.521 0 0 1-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z"/>
                </svg>
              </SocialButton>
              
              <SocialButton 
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareableLink)}`}
                target="_blank"
                rel="noopener noreferrer"
                color="#4267B2"
                aria-label="Share on Facebook"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 3H4a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h8.615v-6.96h-2.338v-2.725h2.338v-2c0-2.325 1.42-3.592 3.5-3.592.699-.002 1.399.034 2.095.107v2.42h-1.435c-1.128 0-1.348.538-1.348 1.325v1.735h2.697l-.35 2.725h-2.348V21H20a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1z"/>
                </svg>
              </SocialButton>
              
              <SocialButton 
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareableLink)}`}
                target="_blank"
                rel="noopener noreferrer"
                color="#0077B5"
                aria-label="Share on LinkedIn"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/>
                </svg>
              </SocialButton>
            </SocialButtons>
          </SocialShareSection>
          
          <DirectLinkSection>
            <PlayButton href={shareableLink} target="_blank" rel="noopener noreferrer">
              Play Game
            </PlayButton>
          </DirectLinkSection>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: ${fadeIn} 0.3s ease-out;
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 10px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  animation: ${slideUp} 0.4s ease-out;
  
  

`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #eeeeee;
`;

const ModalTitle = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: 22px;
  color: #111111;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #555555;
  padding: 0;
  line-height: 1;
  
  &:hover {
    color: #000000;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const GameInfoSection = styled.div`
  margin-bottom: 24px;
`;

const GameTitle = styled.h3`
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: 18px;
  color: #111111;
  margin: 0 0 8px 0;
`;

const GameDescription = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 16px;
  color: #555555;
  margin: 0;
  line-height: 1.5;
`;

const ShareLinkSection = styled.div`
  margin-bottom: 24px;
`;

const ShareLinkLabel = styled.div`
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 500;
  font-size: 16px;
  color: #333333;
  margin-bottom: 8px;
`;

const ShareLinkContainer = styled.div`
  display: flex;
  border: 1px solid #dddddd;
  border-radius: 8px;
  overflow: hidden;
`;

const ShareLink = styled.input`
  flex: 1;
  padding: 12px 16px;
  border: none;
  outline: none;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  color: #333333;
`;

const CopyButton = styled.button`
  padding: 0 20px;
  background-color: #E806941A;
  color: black;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 800;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
  border: 1px solid #E80694;
  border-radius: 10px;
`;

const SocialShareSection = styled.div`
  margin-bottom: 24px;
`;

const SocialShareLabel = styled.div`
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 500;
  font-size: 16px;
  color: #333333;
  margin-bottom: 12px;
`;

const SocialButtons = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
`;

const SocialButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  background-color: ${props => props.color || '#333'};
  color: white;
  border-radius: 50%;
  text-decoration: none;
  font-size: 14px;
  transition: opacity 0.2s, transform 0.2s;
  
  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
  }
`;

const DirectLinkSection = styled.div`
  display: flex;
  justify-content: center;
`;

const PlayButton = styled.a`
  display: inline-block;
  padding: 12px 30px;
  background-color: #E806941A;
  color: #111111;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: 16px;
  border: 1px solid #E80694;
  border-radius: 10px;
  text-decoration: none;
  transition: transform 0.2s, background-color 0.2s;
  margin-bottom: 20px;
`;

export default ShareModal; 